import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faImage,
} from '@fortawesome/free-solid-svg-icons'

// Format ISO date string to "23 July 2026" format
const formatDateDisplay = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return dateString;
  }
};

// Convert date display to ISO format for API
const formatDateISO = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch {
    return dateString;
  }
};

function Admin() {
  const [tab, setTab] = useState('import');
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('');

  // Add Race form state
  const [raceName, setRaceName] = useState('');
  const [raceLocation, setRaceLocation] = useState('');
  const [raceStart, setRaceStart] = useState('');
  const [raceEnd, setRaceEnd] = useState('');

  // Add User To Race
  const [users, setUsers] = useState([]);
  const [races, setRaces] = useState([]);
  const [allRaces, setAllRaces] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRace, setSelectedRace] = useState('');
  const [attending, setAttending] = useState([]);
  const [notAttending, setNotAttending] = useState([]);
  // Hotel form state
  const [hotelName, setHotelName] = useState('');
  const [hotelCheckIn, setHotelCheckIn] = useState('');
  const [hotelCheckOut, setHotelCheckOut] = useState('');
  const [hotelAddress, setHotelAddress] = useState('');
  const [hotelNotes, setHotelNotes] = useState('');
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  
  // User management form state
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userAdmin, setUserAdmin] = useState(false);
  const [selectedUserEdit, setSelectedUserEdit] = useState(null);

  // Airport form state
  const [airports, setAirports] = useState([]);
  const [airportCode, setAirportCode] = useState('');
  const [airportName, setAirportName] = useState('');

  // Race image upload state
  const [raceImage, setRaceImage] = useState(null);
  const [raceImageName, setRaceImageName] = useState(null);

  // Garage PDF upload state
  const [garagePDF, setGaragePDF] = useState(null);
  const [garagePDFName, setGaragePDFName] = useState(null);
  const [hasGaragePDF, setHasGaragePDF] = useState(false);

  // Track info upload state
  const [trackInfo, setTrackInfo] = useState(null);
  const [trackInfoName, setTrackInfoName] = useState(null);

  // Time schedule upload state
  const [timeSchedule, setTimeSchedule] = useState(null);
  const [timeScheduleName, setTimeScheduleName] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setUsers)
      .catch(() => {});
    fetch('/api/races', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { setRaces(data); setAllRaces(data); })
      .catch(() => {});
    fetch('/api/admin/airports', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setAirports)
      .catch(() => {});
  }, []);

  const loadAttendance = async (raceId) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSelectedRace(raceId);
    try {
      const res = await fetch(`/api/races/${raceId}/attendees`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAttending(data || []);
      // compute not attending
      const attendingIds = new Set((data || []).map(u => u.id));
      setNotAttending(users.filter(u => !attendingIds.has(u.id)));
    } catch (e) {
      setAttending([]);
      setNotAttending(users);
    }
  };

  const loadHotels = async (raceId) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`/api/races/${raceId}/hotels`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      // Deduplicate by hotel_name - keep only first entry per unique name
      const uniqueHotels = {};
      (data || []).forEach(h => {
        if (!uniqueHotels[h.hotel_name]) {
          uniqueHotels[h.hotel_name] = h;
        }
      });
      setHotels(Object.values(uniqueHotels));
      setSelectedHotel(null);
    } catch (e) {
      setHotels([]);
      setSelectedHotel(null);
    }
  };

  const addUserToRace = async (userId) => {
    if (!selectedRace) { setStatus('Select a race first'); return; }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/races/${selectedRace}/attendees`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ user_id: userId })
      });
      const data = await res.json();
      setStatus(data.msg || (res.ok ? 'Added' : 'Failed'));
      await loadAttendance(selectedRace);
    } catch (e) { setStatus('Error'); }
  };

  const removeUserFromRace = async (userId) => {
    if (!selectedRace) { setStatus('Select a race first'); return; }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/races/${selectedRace}/attendees`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ user_id: userId })
      });
      const data = await res.json();
      setStatus(data.msg || (res.ok ? 'Removed' : 'Failed'));
      await loadAttendance(selectedRace);
    } catch (e) { setStatus('Error'); }
  };

  const checkGaragePDF = async (raceId) => {
    try {
      const garageRes = await fetch(`/assets/race-info/${raceId}/garage-info.png`, { method: 'HEAD' });
      setHasGaragePDF(garageRes.ok);
    } catch (e) {
      setHasGaragePDF(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      setStatus('Please select a file');
      return;
    }
    const token = localStorage.getItem('token');
    const form = new FormData();
    form.append('file', file);
    setStatus('Uploading...');
    try {
      const res = await fetch('/api/admin/upload-ics', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const data = await res.json();
      setStatus(res.ok ? data.msg || 'Imported' : data.msg || 'Failed to import');
    } catch (err) {
      console.error(err);
      setStatus('Upload failed');
    }
  };

  const handleAddRace = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/races', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: raceName, location: raceLocation, start_date: raceStart, end_date: raceEnd })
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('Race added');
        setRaces(prev => [...prev, data.race]);
        setAllRaces(prev => [...prev, data.race]);
      } else setStatus(data.msg || 'Failed');
    } catch (e) { setStatus('Error'); }
  };

  const handleAddUserToRace = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/races/${selectedRace}/attendees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: selectedUser })
      });
      const data = await res.json();
      setStatus(res.ok ? data.msg || 'Added' : data.msg || 'Failed');
    } catch (e) { setStatus('Error'); }
  };

  const handleDeleteRace = async () => {
    if (!selectedRace) {
      setStatus('Select a race first');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this race?')) {
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/admin/delete_race', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ race_id: selectedRace })
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('Race deleted');
        setRaces(prev => prev.filter(r => r.id !== selectedRace));
        setAllRaces(prev => prev.filter(r => r.id !== selectedRace));
        setSelectedRace('');
        setRaceName('');
        setRaceLocation('');
        setRaceStart('');
        setRaceEnd('');
      } else {
        setStatus(data.msg || 'Failed to delete race');
      }
    } catch (e) {
      setStatus('Error deleting race');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Admin Console</h1>

      <div className="mb-6 border-b">
        <nav className="flex gap-4">
          <button className={`py-2 px-4 ${tab==='import' ? 'border-b-2 border-blue-600' : ''}`} onClick={() => setTab('import')}>Import ICS</button>
          <button className={`py-2 px-4 ${tab==='add-race' ? 'border-b-2 border-blue-600' : ''}`} onClick={() => setTab('add-race')}>Add Race</button>
          <button className={`py-2 px-4 ${tab==='add-user-to-race' ? 'border-b-2 border-blue-600' : ''}`} onClick={() => setTab('add-user-to-race')}>Add User To Race</button>
          <button className={`py-2 px-4 ${tab==='add-hotel' ? 'border-b-2 border-blue-600' : ''}`} onClick={() => setTab('add-hotel')}>Add Hotel</button>
          <button className={`py-2 px-4 ${tab==='edit-race' ? 'border-b-2 border-blue-600' : ''}`} onClick={() => setTab('edit-race')}>Edit Race</button>
          <button className={`py-2 px-4 ${tab==='manage-users' ? 'border-b-2 border-blue-600' : ''}`} onClick={() => setTab('manage-users')}>Manage Users</button>
          <button className={`py-2 px-4 ${tab==='airports' ? 'border-b-2 border-blue-600' : ''}`} onClick={() => setTab('airports')}> Airports</button>
        </nav>
      </div>

      {tab === 'import' && (
        <div>
          <h2 className="text-xl font-semibold mb-2">Import races from ICS</h2>
          <div className="mb-4">
            <input type="file" accept=".ics" onChange={handleFileChange} />
          </div>
          <div>
            <button onClick={handleUpload} className="bg-blue-600 text-white px-4 py-2 rounded">Add races</button>
          </div>
        </div>
      )}

      {tab === 'add-race' && (
        <div>
          <h2 className="text-xl font-semibold mb-2">Add Race</h2>
          <div className="grid grid-cols-2 gap-4 max-w-xl">
            <input value={raceName} onChange={e => setRaceName(e.target.value)} placeholder="Name" className="p-2 border" />
            <input value={raceLocation} onChange={e => setRaceLocation(e.target.value)} placeholder="Location" className="p-2 border" />
            <div>
              <label className="text-sm text-gray-600 block mb-1">Start Date</label>
              <input 
                type="date" 
                value={raceStart} 
                onChange={e => setRaceStart(e.target.value)} 
                className="p-2 border w-full" 
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 block mb-1">End Date</label>
              <input 
                type="date" 
                value={raceEnd} 
                onChange={e => setRaceEnd(e.target.value)} 
                className="p-2 border w-full" 
              />
            </div>
          </div>
          <div className="mt-4">
            <button onClick={handleAddRace} className="bg-green-600 text-white px-4 py-2 rounded">Create Race</button>
          </div>
        </div>
      )}

      {tab === 'add-user-to-race' && (
        <div>
          <h2 className="text-xl font-semibold mb-2">Manage Attendance</h2>
          <div className="grid grid-cols-2 gap-6">
            {/* Left: search/select race */}
            <div>
              <input
                placeholder="Search races"
                className="w-full p-2 border mb-2"
                onChange={e => {
                  const q = e.target.value.toLowerCase();
                  if (!q) {
                    setRaces(allRaces);
                  } else {
                    setRaces(allRaces.filter(x => (x.name || '').toLowerCase().includes(q) || (x.location || '').toLowerCase().includes(q)));
                  }
                }}
              />
              <div className="max-h-96 overflow-auto border p-2">
                {races.map(r => (
                  <div key={r.id} className={`p-2 cursor-pointer ${selectedRace==r.id ? 'bg-blue-50' : ''}`} onClick={() => { setSelectedRace(r.id); loadAttendance(r.id); }}>
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-sm text-gray-500">{r.location}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: attendees / not attending lists */}
            <div>
              <div className="mb-2 font-semibold">Attendees ({attending.length})</div>
              <div className="h-44 overflow-auto border p-2 mb-4">
                {attending.length === 0 && <div className="text-sm text-gray-500">No attendees</div>}
                {attending.map(u => (
                  <label key={u.id} className="flex items-center gap-2 p-1">
                    <input type="checkbox" checked={true} onChange={() => removeUserFromRace(u.id)} />
                    <span>{u.name} — {u.email}</span>
                  </label>
                ))}
              </div>

              <div className="mb-2 font-semibold">Not Attending</div>
              <div className="h-44 overflow-auto border p-2">
                {notAttending.length === 0 && <div className="text-sm text-gray-500">All users attending</div>}
                {notAttending.map(u => (
                  <label key={u.id} className="flex items-center gap-2 p-1">
                    <input type="checkbox" checked={false} onChange={() => addUserToRace(u.id)} />
                    <span>{u.name} — {u.email}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'add-hotel' && (
        <div>
          <h2 className="text-xl font-semibold mb-2">Manage Hotels</h2>
          <div className="grid grid-cols-2 gap-6">
            {/* Left: select race */}
            <div>
              <input
                placeholder="Search races"
                className="w-full p-2 border mb-2"
                onChange={e => {
                  const q = e.target.value.toLowerCase();
                  if (!q) setRaces(allRaces);
                  else setRaces(allRaces.filter(x => (x.name || '').toLowerCase().includes(q) || (x.location || '').toLowerCase().includes(q)));
                }}
              />
              <div className="max-h-96 overflow-auto border p-2">
                {races.map(r => (
                  <div key={r.id} className={`p-2 cursor-pointer ${selectedRace==r.id ? 'bg-blue-50' : ''}`} onClick={() => { setSelectedRace(r.id); loadAttendance(r.id); loadHotels(r.id); }}>
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-sm text-gray-500">{r.location}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: hotel form + attendees */}
            <div>
              <div className="mb-2 font-semibold">{selectedHotel !== null ? 'Edit Hotel' : 'New Hotel'}</div>
              
              {hotels.length > 0 && (
                <div className="mb-4">
                  <label className="text-sm text-gray-600 block mb-1">Select existing hotel</label>
                  <select 
                    value={selectedHotel ?? ''} 
                    onChange={(e) => {
                      const idx = e.target.value === '' ? null : parseInt(e.target.value);
                      if (idx !== null) {
                        const h = hotels[idx];
                        setSelectedHotel(idx);
                        setHotelName(h.hotel_name);
                        setHotelCheckIn(h.check_in || '');
                        setHotelCheckOut(h.check_out || '');
                        setHotelAddress(h.address || '');
                        setHotelNotes(h.hotel_notes || '');
                        setSelectedUser('');
                      } else {
                        setSelectedHotel(null);
                        setHotelName('');
                        setHotelCheckIn('');
                        setHotelCheckOut('');
                        setHotelAddress('');
                        setHotelNotes('');
                        setSelectedUser('');
                      }
                    }}
                    className="w-full p-2 border"
                  >
                    <option value="">Create new hotel</option>
                    {hotels.map((h, idx) => (
                      <option key={idx} value={idx}>{h.hotel_name} — {h.address}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="grid gap-2 mb-4">
                <input value={hotelName} onChange={e => setHotelName(e.target.value)} placeholder="Hotel name" className="p-2 border" />
                <label className="text-sm text-gray-600">Check-in</label>
                <input value={hotelCheckIn} onChange={e => setHotelCheckIn(e.target.value)} type="datetime-local" className="p-2 border" />
                <label className="text-sm text-gray-600">Check-out</label>
                <input value={hotelCheckOut} onChange={e => setHotelCheckOut(e.target.value)} type="datetime-local" className="p-2 border" />
                <input value={hotelAddress} onChange={e => setHotelAddress(e.target.value)} placeholder="Address" className="p-2 border" />
                <input value={hotelNotes} onChange={e => setHotelNotes(e.target.value)} placeholder="Notes" className="p-2 border" />
              </div>

              <div className="mb-4">
                <div className="mb-2 font-semibold text-sm">Attendees (click to select)</div>
                <div className="h-44 overflow-auto border p-2 mb-2">
                  {attending.length === 0 && <div className="text-sm text-gray-500">No attendees loaded</div>}
                  {attending.map(u => (
                    <div key={u.id} className={`p-1 cursor-pointer text-sm ${selectedUser==u.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`} onClick={() => setSelectedUser(u.id)}>
                      <div>{u.name} — {u.email}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {selectedHotel !== null ? (
                  <>
                    <button onClick={async () => {
                      if (!selectedRace) { setStatus('Select a race first'); return; }
                      const h = hotels[selectedHotel];
                      const token = localStorage.getItem('token');
                      try {
                        const res = await fetch(`/api/admin/update_hotel_by_name/${selectedRace}`, {
                          method: 'POST', 
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ original_name: h.hotel_name, hotel_name: hotelName, check_in: hotelCheckIn, check_out: hotelCheckOut, address: hotelAddress, hotel_notes: hotelNotes })
                        });
                        const data = await res.json();
                        if (res.ok) {
                          setStatus(data.msg || 'Hotel updated');
                          await loadHotels(selectedRace);
                        } else setStatus(data.msg || 'Failed to update');
                      } catch (e) { setStatus('Error updating hotel'); }
                    }} className="bg-purple-600 text-white px-3 py-2 rounded text-sm">Update Hotel</button>

                    <button onClick={() => {
                      setSelectedHotel(null);
                      setHotelName('');
                      setHotelCheckIn('');
                      setHotelCheckOut('');
                      setHotelAddress('');
                      setHotelNotes('');
                      setSelectedUser('');
                    }} className="bg-gray-400 text-white px-3 py-2 rounded text-sm">Clear Selection</button>
                  </>
                ) : (
                  <>
                    <button onClick={async () => {
                      if (!selectedRace) { setStatus('Select a race first'); return; }
                      const targetUser = selectedUser || (attending[0] && attending[0].id);
                      if (!targetUser) { setStatus('Select or load attendees first'); return; }
                      const token = localStorage.getItem('token');
                      try {
                        const res = await fetch('/api/hotels', {
                          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ user_id: targetUser, race_id: selectedRace, hotel_name: hotelName, check_in: hotelCheckIn, check_out: hotelCheckOut, address: hotelAddress, notes: hotelNotes })
                        });
                        const data = await res.json();
                        if (res.ok) {
                          setStatus(data.msg || 'Hotel assignment created');
                          await loadHotels(selectedRace);
                          setSelectedUser('');
                        } else setStatus(data.msg || 'Failed');
                      } catch (e) { setStatus('Error creating hotel assignment'); }
                    }} className="bg-blue-600 text-white px-3 py-2 rounded text-sm">Add Selected User</button>

                    <button onClick={async () => {
                      if (!selectedRace) { setStatus('Select a race first'); return; }
                      const token = localStorage.getItem('token');
                      try {
                        const res = await fetch(`/api/races/${selectedRace}/hotels/bulk`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ hotel_name: hotelName, check_in: hotelCheckIn, check_out: hotelCheckOut, address: hotelAddress, notes: hotelNotes })
                        });
                        const data = await res.json();
                        if (res.ok) {
                          setStatus(data.msg || 'Hotel assigned to all attendees');
                          await loadHotels(selectedRace);
                        } else setStatus(data.msg || 'Failed');
                      } catch (e) { setStatus('Error assigning hotel'); }
                    }} className="bg-green-600 text-white px-3 py-2 rounded text-sm">Add All Attendees</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'edit-race' && (
        <div>
          <h2 className="text-xl font-semibold mb-2">Edit Race</h2>
          <div className="grid grid-cols-2 gap-6">
            {/* Left: select race */}
            <div>
              <input
                placeholder="Search races"
                className="w-full p-2 border mb-2"
                onChange={e => {
                  const q = e.target.value.toLowerCase();
                  if (!q) setRaces(allRaces);
                  else setRaces(allRaces.filter(x => (x.name || '').toLowerCase().includes(q) || (x.location || '').toLowerCase().includes(q)));
                }}
              />
              <div className="max-h-96 overflow-auto border p-2">
                {races.map(r => (
                  <div
                    key={r.id}
                    className={`p-2 cursor-pointer ${selectedRace === r.id ? 'bg-blue-50' : ''}`}
                    onClick={() => {
                      setSelectedRace(r.id);
                      setRaceName(r.name);
                      setRaceLocation(r.location);
                      setRaceStart(r.start_date);
                      setRaceEnd(r.end_date);
                      setGaragePDF(null);
                      setGaragePDFName(null);
                      setTrackInfo(null);
                      setTrackInfoName(null);
                      setTimeSchedule(null);
                      setTimeScheduleName(null);
                      checkGaragePDF(r.id);
                    }}
                  >
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-sm text-gray-500">{r.location}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: edit race form */}
            <div>
              <div className="grid gap-2">
                <input
                  value={raceName}
                  onChange={e => setRaceName(e.target.value)}
                  placeholder="Race name"
                  className="p-2 border"
                />
                <input
                  value={raceLocation}
                  onChange={e => setRaceLocation(e.target.value)}
                  placeholder="Location"
                  className="p-2 border"
                />
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Start Date</label>
                  <input
                    type="date"
                    value={raceStart}
                    onChange={e => setRaceStart(e.target.value)}
                    className="p-2 border w-full"
                  />
                  {raceStart && <div className="text-sm text-gray-500 mt-1">{formatDateDisplay(raceStart)}</div>}
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-1">End Date</label>
                  <input
                    type="date"
                    value={raceEnd}
                    onChange={e => setRaceEnd(e.target.value)}
                    className="p-2 border w-full"
                  />
                  {raceEnd && <div className="text-sm text-gray-500 mt-1">{formatDateDisplay(raceEnd)}</div>}
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Race Image</label>
                  <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center cursor-pointer hover:border-blue-400 transition">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setRaceImage(file);
                          setRaceImageName(file.name);
                        }
                      }}
                      className="hidden"
                      id="race-image-input"
                    />
                    <label htmlFor="race-image-input" className="cursor-pointer">
                      <FontAwesomeIcon icon={faImage} className="text-2xl text-gray-400 mb-2 block" />
                      <div className="text-sm text-gray-600">Click to upload or drag image here</div>
                    </label>
                  </div>
                  {raceImageName && (
                    <div className="mt-2">
                      <div className="text-sm text-gray-600"> {raceImageName}</div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Garage Information Image</label>
                  <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center cursor-pointer hover:border-blue-400 transition">
                    <input
                      type="file"
                      accept=".png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (!file.name.toLowerCase().endsWith('.png')) {
                            setStatus('Only PNG files are allowed');
                            return;
                          }
                          setGaragePDF(file);
                          setGaragePDFName(file.name);
                        }
                      }}
                      className="hidden"
                      id="garage-pdf-input"
                    />
                    <label htmlFor="garage-pdf-input" className="cursor-pointer">
                      <FontAwesomeIcon icon={faImage} className="text-2xl text-gray-400 mb-2 block" />
                      <div className="text-sm text-gray-600">Click to upload PNG image</div>
                    </label>
                  </div>
                  {garagePDFName && (
                    <div className="mt-2">
                      <div className="text-sm text-gray-600"> {garagePDFName}</div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Track Info Image</label>
                  <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center cursor-pointer hover:border-blue-400 transition">
                    <input
                      type="file"
                      accept=".png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (!file.name.toLowerCase().endsWith('.png')) {
                            setStatus('Only PNG files are allowed');
                            return;
                          }
                          setTrackInfo(file);
                          setTrackInfoName(file.name);
                        }
                      }}
                      className="hidden"
                      id="track-info-input"
                    />
                    <label htmlFor="track-info-input" className="cursor-pointer">
                      <FontAwesomeIcon icon={faImage} className="text-2xl text-gray-400 mb-2 block" />
                      <div className="text-sm text-gray-600">Click to upload PNG image</div>
                    </label>
                  </div>
                  {trackInfoName && (
                    <div className="mt-2">
                      <div className="text-sm text-gray-600"> {trackInfoName}</div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Time Schedule Image</label>
                  <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center cursor-pointer hover:border-blue-400 transition">
                    <input
                      type="file"
                      accept=".png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (!file.name.toLowerCase().endsWith('.png')) {
                            setStatus('Only PNG files are allowed');
                            return;
                          }
                          setTimeSchedule(file);
                          setTimeScheduleName(file.name);
                        }
                      }}
                      className="hidden"
                      id="time-schedule-input"
                    />
                    <label htmlFor="time-schedule-input" className="cursor-pointer">
                      <FontAwesomeIcon icon={faImage} className="text-2xl text-gray-400 mb-2 block" />
                      <div className="text-sm text-gray-600">Click to upload PNG image</div>
                    </label>
                  </div>
                  {timeScheduleName && (
                    <div className="mt-2">
                      <div className="text-sm text-gray-600"> {timeScheduleName}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={async () => {
                    if (!selectedRace) {
                      setStatus('Select a race first');
                      return;
                    }
                    const token = localStorage.getItem('token');
                    try {
                      // First update race details
                      const res = await fetch('/api/admin/edit_race', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({
                          race_id: selectedRace,
                          name: raceName,
                          location: raceLocation,
                          start_date: raceStart,
                          end_date: raceEnd,
                        }),
                      });
                      const data = await res.json();
                      
                      // Then upload image if one was selected
                      if (raceImage && res.ok) {
                        const formData = new FormData();
                        formData.append('file', raceImage);
                        const imgRes = await fetch(`/api/admin/races/${selectedRace}/image`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                          body: formData,
                        });
                        const imgData = await imgRes.json();
                        if (!imgRes.ok) {
                          setStatus(`Race updated but image upload failed: ${imgData.msg || 'Failed'}`);
                          return;
                        }
                        setRaceImage(null);
                        setRaceImageName(null);
                      }
                      
                      // Upload garage PDF if one was selected
                      if (garagePDF && res.ok) {
                        const pdfFormData = new FormData();
                        pdfFormData.append('file', garagePDF);
                        pdfFormData.append('race_id', selectedRace);
                        const pdfRes = await fetch('/api/admin/add_garage_pdf', {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                          body: pdfFormData,
                        });
                        const pdfData = await pdfRes.json();
                        if (!pdfRes.ok) {
                          setStatus(`Race updated but garage upload failed: ${pdfData.msg || 'Failed'}`);
                          return;
                        }
                        setGaragePDF(null);
                        setGaragePDFName(null);
                      }

                      // Upload track info if one was selected
                      if (trackInfo && res.ok) {
                        const trackFormData = new FormData();
                        trackFormData.append('file', trackInfo);
                        trackFormData.append('race_id', selectedRace);
                        const trackRes = await fetch('/api/admin/add_track_info', {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                          body: trackFormData,
                        });
                        const trackData = await trackRes.json();
                        if (!trackRes.ok) {
                          setStatus(`Race updated but track info upload failed: ${trackData.msg || 'Failed'}`);
                          return;
                        }
                        setTrackInfo(null);
                        setTrackInfoName(null);
                      }

                      // Upload time schedule if one was selected
                      if (timeSchedule && res.ok) {
                        const schedFormData = new FormData();
                        schedFormData.append('file', timeSchedule);
                        schedFormData.append('race_id', selectedRace);
                        const schedRes = await fetch('/api/admin/add_time_schedule', {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                          body: schedFormData,
                        });
                        const schedData = await schedRes.json();
                        if (!schedRes.ok) {
                          setStatus(`Race updated but time schedule upload failed: ${schedData.msg || 'Failed'}`);
                          return;
                        }
                        setTimeSchedule(null);
                        setTimeScheduleName(null);
                      }
                      
                      setStatus(res.ok ? data.msg || 'Race updated' : data.msg || 'Failed to update race');
                      if (res.ok) {
                        const updatedRace = {
                          ...races.find(r => r.id === selectedRace),
                          name: raceName,
                          location: raceLocation,
                          start_date: raceStart,
                          end_date: raceEnd,
                          start_ts: new Date(raceStart).getTime(),
                        };
                        setRaces(prev => {
                          const updatedRaces = prev.map(r => (r.id === selectedRace ? updatedRace : r));
                          return updatedRaces.sort((a, b) => a.start_ts - b.start_ts); // Sort after updating
                        });
                        setAllRaces(prev => {
                          const updatedAllRaces = prev.map(r => (r.id === selectedRace ? updatedRace : r));
                          return updatedAllRaces.sort((a, b) => a.start_ts - b.start_ts); // Sort after updating
                        });
                      }
                    } catch (e) {
                      setStatus('Error updating race');
                    }
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                  Save Changes
                </button>
                <button
                  onClick={handleDeleteRace}
                  className="bg-red-600 text-white px-4 py-2 rounded"
                >
                  Delete Race
                </button>
              </div>
            </div>
          </div>
          {status && <p className="mt-4 text-red-500">{status}</p>}
        </div>
      )}

      {tab === 'manage-users' && (
        <div>
          <h2 className="text-xl font-semibold mb-2">Manage Users</h2>
          <div className="grid grid-cols-2 gap-6">
            {/* Left: user list */}
            <div>
              <div className="mb-2 font-semibold">Users</div>
              <div className="max-h-96 overflow-auto border p-2">
                {users.map(u => (
                  <div
                    key={u.id}
                    className={`p-2 cursor-pointer border-b ${selectedUserEdit?.id === u.id ? 'bg-blue-50' : ''}`}
                    onClick={() => {
                      setSelectedUserEdit(u);
                      setUserName(u.name);
                      setUserEmail(u.email);
                      setUserPassword('');
                      setUserAdmin(u.admin || false);
                    }}
                  >
                    <div className="font-semibold">{u.name}</div>
                    <div className="text-sm text-gray-500">{u.email}</div>
                    {u.admin && <div className="text-xs text-red-600">Admin</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: user form */}
            <div>
              <div className="mb-4">
                <h3 className="font-semibold mb-2">{selectedUserEdit ? 'Edit User' : 'Add New User'}</h3>
                <div className="grid gap-2">
                  <input
                    value={userName}
                    onChange={e => setUserName(e.target.value)}
                    placeholder="Full Name"
                    className="p-2 border"
                  />
                  <input
                    value={userEmail}
                    onChange={e => setUserEmail(e.target.value)}
                    placeholder="Email"
                    className="p-2 border"
                  />
                  <input
                    value={userPassword}
                    onChange={e => setUserPassword(e.target.value)}
                    placeholder={selectedUser ? "Leave blank to keep password" : "Password"}
                    type="password"
                    className="p-2 border"
                  />
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={userAdmin}
                      onChange={e => setUserAdmin(e.target.checked)}
                    />
                    <span>Admin</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-2">
                {!selectedUserEdit ? (
                  <button
                    onClick={async () => {
                      if (!userName || !userEmail || !userPassword) {
                        setStatus('Please fill all fields');
                        return;
                      }
                      const token = localStorage.getItem('token');
                      try {
                        const res = await fetch('/api/admin/add_user', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({
                            name: userName,
                            email: userEmail,
                            password: userPassword,
                            admin: userAdmin,
                          }),
                        });
                        const data = await res.json();
                        if (res.ok) {
                          setStatus('User added successfully');
                          setUserName('');
                          setUserEmail('');
                          setUserPassword('');
                          setUserAdmin(false);
                          // Refresh user list
                          const usersRes = await fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
                          const usersData = await usersRes.json();
                          setUsers(usersData || []);
                        } else {
                          setStatus(data.msg || 'Failed to add user');
                        }
                      } catch (e) {
                        setStatus('Error adding user');
                      }
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded"
                  >
                    Add User
                  </button>
                ) : (
                  <>
                    <button
                      onClick={async () => {
                        if (!userName || !userEmail) {
                          setStatus('Please fill name and email');
                          return;
                        }
                        const token = localStorage.getItem('token');
                        try {
                          const res = await fetch(`/api/admin/edit_user/${selectedUserEdit.id}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            body: JSON.stringify({
                              name: userName,
                              email: userEmail,
                              password: userPassword || undefined,
                              admin: userAdmin,
                            }),
                          });
                          const data = await res.json();
                          if (res.ok) {
                            setStatus('User updated successfully');
                            setSelectedUserEdit(null);
                            setUserName('');
                            setUserEmail('');
                            setUserPassword('');
                            setUserAdmin(false);
                            // Refresh user list
                            const usersRes = await fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
                            const usersData = await usersRes.json();
                            setUsers(usersData || []);
                          } else {
                            setStatus(data.msg || 'Failed to update user');
                          }
                        } catch (e) {
                          setStatus('Error updating user');
                        }
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete user ${selectedUserEdit.name}? This cannot be undone.`)) {
                          return;
                        }
                        const token = localStorage.getItem('token');
                        try {
                          const res = await fetch(`/api/admin/delete_user/${selectedUserEdit.id}`, {
                            method: 'DELETE',
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          const data = await res.json();
                          if (res.ok) {
                            setStatus('User deleted successfully');
                            setSelectedUserEdit(null);
                            setUserName('');
                            setUserEmail('');
                            setUserPassword('');
                            setUserAdmin(false);
                            // Refresh user list
                            const usersRes = await fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
                            const usersData = await usersRes.json();
                            setUsers(usersData || []);
                          } else {
                            setStatus(data.msg || 'Failed to delete user');
                          }
                        } catch (e) {
                          setStatus('Error deleting user');
                        }
                      }}
                      className="bg-red-600 text-white px-4 py-2 rounded"
                    >
                      Delete User
                    </button>
                    <button
                      onClick={() => {
                        setSelectedUserEdit(null);
                        setUserName('');
                        setUserEmail('');
                        setUserPassword('');
                        setUserAdmin(false);
                      }}
                      className="bg-gray-400 text-white px-4 py-2 rounded"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'airports' && (
        <div>
          <h2 className="text-xl font-semibold mb-2"> Add New Airports</h2>
          <div className="grid grid-cols-2 gap-6">
            {/* Left: airports list */}
            <div>
              <div className="mb-2 font-semibold">Available Airports ({airports.length})</div>
              <div className="max-h-96 overflow-auto border p-2">
                {airports.length === 0 ? (
                  <div className="text-sm text-gray-500">No airports loaded</div>
                ) : (
                  airports.map((a, idx) => (
                    <div key={idx} className="p-2 border-b hover:bg-gray-50">
                      <div className="font-semibold text-blue-600">{a.code}</div>
                      <div className="text-sm text-gray-700">{a.name}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right: add new airport form */}
            <div>
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Add New Airport</h3>
                <div className="grid gap-2">
                  <label className="text-sm text-gray-600">Airport Code</label>
                  <input
                    value={airportCode}
                    onChange={e => setAirportCode(e.target.value.toUpperCase())}
                    placeholder="e.g., MXP, FCO, VIE"
                    className="p-2 border"
                    maxLength="5"
                  />
                  <label className="text-sm text-gray-600">Airport Name</label>
                  <input
                    value={airportName}
                    onChange={e => setAirportName(e.target.value)}
                    placeholder="e.g., Milan Malpensa Airport"
                    className="p-2 border"
                  />
                </div>
              </div>

              <button
                onClick={async () => {
                  if (!airportCode || !airportName) {
                    setStatus('Please fill in both airport code and name');
                    return;
                  }
                  if (airports.some(a => a.code === airportCode)) {
                    setStatus(`Airport code ${airportCode} already exists`);
                    return;
                  }
                  const token = localStorage.getItem('token');
                  try {
                    const res = await fetch('/api/admin/airports', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({
                        code: airportCode,
                        name: airportName,
                      }),
                    });
                    const data = await res.json();
                    if (res.ok) {
                      setStatus('Airport added successfully');
                      setAirportCode('');
                      setAirportName('');
                      // Refresh list
                      const airportsRes = await fetch('/api/admin/airports', { headers: { Authorization: `Bearer ${token}` } });
                      const airportsData = await airportsRes.json();
                      setAirports(airportsData || []);
                    } else {
                      setStatus(data.msg || 'Failed to add airport');
                    }
                  } catch (e) {
                    setStatus('Error adding airport');
                  }
                }}
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                Add Airport
              </button>
            </div>
          </div>
        </div>
      )}

      {status && <p className="mt-4">{status}</p>}
    </div>
  );
}

export default Admin;
